import * as React from 'react';
import { Form, FormInstance, FormProps, Modal, ModalProps } from 'antd';
import { Observer, useLocalObservable } from 'mobx-react-lite';

export type ModelComponentProps = {
  title?: React.ReactNode;
  form?: FormInstance;
  formData?: any;
  formProps?: FormProps;
  modalProps?: ModalProps;
  onSubmit?: (values: any) => Promise<any>;
  onCancel?: () => Promise<any>;
};

export type ModelContentProps = Pick<ModelComponentProps, 'title' | 'formData' | 'form'> & any;

export type ModelOpenType = Pick<ModelComponentProps, 'title' | 'formProps' | 'modalProps'> & {
  [p in string]: any;
}

export type ModalApi = {
  open: (data?: ModelOpenType) => any;
  close: () => any;
}

export type ModelContentType = {
  Content: React.FC<ModelContentProps>;
}

// export type ModelComponentProps = ModelContentProps & {
//   formProps?: FormProps;
//   modalProps?: ModalProps;
//   onSubmit?: (values: any) => Promise<any>;
//   onCancel?: () => Promise<any>;
// };

const ModelComponent: React.FC<ModelComponentProps & ModelContentType> = ({ Content, formData, modalProps, formProps, ...props }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  return (
    <Modal
      {...(modalProps || {})}
      open
      mask
      centered
      confirmLoading={loading}
      maskClosable={false}
      onOk={() => {
        form.submit();
      }}
      onCancel={async () => {
        await props.onCancel?.();
      }}
    >
      <Form
        {...formProps}
        form={form}
        initialValues={formData}
        onFinish={async (values: any) => {
          try {
            setLoading(true);
            await props?.onSubmit?.(values);
          } finally {
            setLoading(false);
          }
        }}
      >
        <Content {...props} form={form} />
      </Form>
    </Modal>
  );
};

function useFormModal(component: React.FC<ModelContentProps>) {
  const store = useLocalObservable(() => ({
    visible: false,
    data: {},
    formProps: {} as FormProps,
    modalProps: {} as ModalProps,
    contentProps: {} as any,
    open({ data, modalProps, formProps, title, ...props }: ModelOpenType = {}) {
      this.visible = true;
      this.data = data || {};
      this.formProps = {
        ...(formProps || {}),
      };
      this.modalProps = {
        ...(modalProps || {}),
      };
      if (title) {
        this.modalProps = {
          ...this.modalProps,
          title: title,
        };
      }
      this.contentProps = {
        ...this.contentProps,
        ...(props || {}),
      };
    },
    close() {
      this.visible = false;
    }
  }));

  const slot = React.useMemo(() => {
    return (props: ModelComponentProps) => (
      <Observer>
        {() => {
          if (!store.visible) {
            return null;
          }

          return (
            <ModelComponent
              {...props}
              {...store.contentProps}
              close={store.close}
              formData={store.data}
              onCancel={async () => {
                const bool = await props.onCancel?.();
                if (bool !== false) {
                  store.close();
                }
              }}
              onSubmit={async (values: any) => {
                const bool = await props.onSubmit?.(values);
                if (bool !== false) {
                  store.close();
                }
              }}
              formProps={{
                ...(props?.formProps || {}),
                ...store.formProps,
              }}
              modalProps={{
                title: props.title,
                ...(props.modalProps || {}),
                ...store.modalProps,
              }}
              Content={component}
            />
          );
        }}
      </Observer>
    );
  }, []);

  return [store, slot] as [ModalApi, React.FC<ModelComponentProps>];
}

export default useFormModal;
